import { useEffect, useState } from 'react';
import { showAlert, showConfirm } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import { BRAND, type Brand } from '@/brand';
import AdminLayout from '../components/AdminLayout';
import { Plus, Trash2, Pencil, Search, X, Users, CheckCircle2, Handshake, Building2, Save } from 'lucide-react';

const TYPES = ['Referral Partner', 'Free Zone', 'Law Firm', 'Bank', 'Accountant', 'Marketing', 'Other'];
const CHIPS: { label: string; match: (type: string) => boolean }[] = [
  { label: 'All', match: () => true },
  { label: 'Referral', match: (t) => t === 'Referral Partner' },
  { label: 'Free zone', match: (t) => t === 'Free Zone' },
  { label: 'Law firm', match: (t) => t === 'Law Firm' },
  { label: 'Bank', match: (t) => t === 'Bank' },
  { label: 'Other', match: (t) => !['Referral Partner', 'Free Zone', 'Law Firm', 'Bank'].includes(t) },
];

type Partner = {
  id: string;
  name: string;
  type: string;
  /** Which admin owns this row. Absent on rows added before brand scoping. */
  brand?: Brand;
  contactName?: string;
  email?: string;
  phone?: string;
  commissionPct?: string;
  status?: 'active' | 'paused' | 'pending';
  notes?: string;
  addedAt?: string;
};

const newId = () => `p_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

const IS_CORP = BRAND === 'corporate';

/* `partners/current` is a single shared doc, so without this every partner showed up
   in BOTH the accounting and the consulting admin. Rows added before brand scoping
   carry no brand and fall to corporate — the same convention AdminIntake uses, so
   they stay visible to someone instead of disappearing from both views.

   Scoping is applied at render only: handleSave writes the whole `partners` state
   back, so the other brand's rows must never be filtered out of state itself. */
const isMine = (p: Partner) => (p.brand ? p.brand === BRAND : IS_CORP);

const statusPill = (status?: Partner['status']) => {
  const s = status || 'active';
  if (s === 'paused') return { cls: 'ax-pill ax-pill-grey', label: 'Paused' };
  if (s === 'pending') return { cls: 'ax-pill ax-pill-amber', label: 'Pending' };
  return { cls: 'ax-pill ax-pill-green', label: 'Active' };
};

type Draft = { name: string; type: string; status: NonNullable<Partner['status']>; contactName: string; email: string };
const emptyDraft = (): Draft => ({ name: '', type: TYPES[0], status: 'active', contactName: '', email: '' });

export default function AdminPartners({ embedded = false, onCount }: { embedded?: boolean; onCount?: (n: number) => void }) {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [chip, setChip] = useState('All');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Draft>(emptyDraft());

  // ── LOAD (preserved verbatim) ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'partners', 'current'));
        if (snap.exists() && Array.isArray(snap.data().partners)) setPartners(snap.data().partners);
      } catch (e) { console.error('load partners failed', e); }
      finally { setLoading(false); }
    })();
  }, []);

  // ── REMOVE (preserved verbatim) ──────────────────────────────────────────
  const removePartner = async (id: string, name: string) => {
    if (!(await showConfirm(`Remove ${name || 'this partner'}?`, { destructive: true, confirmText: 'Remove' }))) return;
    setPartners(ps => ps.filter(p => p.id !== id));
  };

  // ── SAVE (preserved verbatim) ────────────────────────────────────────────
  const handleSave = async () => {
    const clean = partners.filter(p => (p.name || '').trim());
    setSaving(true);
    try {
      await saveAdminConfig('partners/current', { partners: clean });
      setPartners(clean);
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'partners/current', { count: clean.length });
    } catch (e: any) {
      showAlert(e?.code === 'permission-denied'
        ? 'Save failed. Permission denied. This editor needs a Firebase-authenticated admin session.'
        : 'Save failed. Check console.');
    } finally { setSaving(false); }
  };

  // ── ADD / EDIT (same local-state mechanic, surfaced through the modal) ────
  const openAdd = () => { setEditingId(null); setForm(emptyDraft()); setModalOpen(true); };
  const openEdit = (p: Partner) => {
    setEditingId(p.id);
    setForm({ name: p.name || '', type: p.type || TYPES[0], status: p.status || 'active', contactName: p.contactName || '', email: p.email || '' });
    setModalOpen(true);
  };
  const submitModal = () => {
    if (editingId) {
      // spread preserves fields not shown in the modal (phone / commissionPct / notes)
      setPartners(ps => ps.map(p => p.id === editingId ? { ...p, ...form } : p));
    } else {
      setPartners(ps => [{ id: newId(), brand: BRAND, ...form, addedAt: new Date().toISOString() }, ...ps]);
    }
    setModalOpen(false);
  };

  const mine = partners.filter(isMine);

  /* Feeds the count badge on the Partners hub tab — this brand's rows only, and
     only once loaded so the badge doesn't flash a 0 on the way in. */
  useEffect(() => { if (!loading) onCount?.(mine.length); }, [mine.length, loading, onCount]);

  const active = mine.filter(p => p.status !== 'paused').length;
  const referrals = mine.filter(p => p.type === 'Referral Partner').length;

  const activeChip = CHIPS.find(c => c.label === chip);
  const q = query.trim().toLowerCase();
  const filtered = mine.filter(p => {
    if (activeChip && !activeChip.match(p.type || '')) return false;
    if (!q) return true;
    return [p.name, p.type, p.contactName, p.email].some(v => (v || '').toLowerCase().includes(q));
  });

  const kpis = [
    { label: 'Partners', value: mine.length, icon: <Users className="w-5 h-5" /> },
    { label: 'Active', value: active, icon: <CheckCircle2 className="w-5 h-5" /> },
    { label: 'Referral partners', value: referrals, icon: <Handshake className="w-5 h-5" /> },
  ];

  /* Content is separated from the page chrome so AdminPartnersHub can render it
     inside its own shell as a tab, without a second sidebar and topbar. */
  const content = (
    <>
      {/* Page-specific only — the top bar, page header, buttons, stat tiles and
          pills come from adminShell.css. This block used to hardcode #2563EB, so
          the roster stayed blue on the accounting brand. */}
      <style>{`
        .adminx .ax-card { background:#fff; border:1px solid #eef1f5; border-radius:16px; box-shadow:0 1px 2px rgba(16,24,40,.04); overflow:hidden; }
        .adminx .ax-toolbar { padding:18px 20px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; gap:16px; flex-wrap:wrap; }
        .adminx .ax-table { width:100%; border-collapse:collapse; }
        .adminx .ax-table th { text-align:start; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--ax-faint); padding:12px 20px; border-bottom:1px solid #f1f5f9; }
        .adminx .ax-table td { padding:16px 20px; border-bottom:1px solid #f4f6f9; font-size:14px; vertical-align:middle; }
        .adminx .ax-table tr:last-child td { border-bottom:none; }
        .adminx .ax-table tr:hover td { background:#fafbfc; }
        .adminx .ax-pname { font-weight:600; color:var(--ax-ink); }
        .adminx .ax-type { color:#475569; }
        .adminx .ax-contact { color:var(--ax-ink); font-weight:500; }
        .adminx .ax-contact small { display:block; color:var(--ax-faint); font-weight:400; margin-top:2px; }
        .adminx .ax-muted { color:#cbd5e1; }

        .adminx .ax-actions { display:flex; gap:6px; justify-content:flex-end; }
        .adminx .ax-icon-btn { display:inline-flex; align-items:center; gap:6px; border:1px solid #e2e8f0; background:#fff; color:#475569; border-radius:8px; padding:6px 10px; font-size:12px; font-weight:600; cursor:pointer; font-family:inherit; transition:all .15s; }
        .adminx .ax-icon-btn svg { width:14px; height:14px; }
        .adminx .ax-icon-btn:hover { background:#f8fafc; }
        .adminx .ax-icon-btn.danger:hover { background:#fef2f2; color:#dc2626; border-color:#fecaca; }

        .adminx .ax-empty { padding:64px 20px; text-align:center; }
        .adminx .ax-empty svg { width:48px; height:48px; margin:0 auto 12px; color:#e2e8f0; }
        .adminx .ax-empty p { font-size:14px; font-weight:600; color:var(--ax-muted); margin:0 0 4px; }
        .adminx .ax-empty span { font-size:13px; color:var(--ax-faint); }

        .adminx .ax-spin { display:flex; justify-content:center; padding:72px 0; }

        .adminx .ax-overlay { position:fixed; inset:0; background:rgba(15,23,42,.45); display:flex; align-items:center; justify-content:center; padding:20px; z-index:50; }
        .adminx .ax-modal { background:#fff; border-radius:18px; width:100%; max-width:480px; box-shadow:0 20px 50px rgba(16,24,40,.25); overflow:hidden; }
        .adminx .ax-modal-head { display:flex; align-items:center; justify-content:space-between; padding:20px 22px; border-bottom:1px solid #f1f5f9; }
        .adminx .ax-modal-head h3 { margin:0; font-size:17px; font-weight:700; }
        .adminx .ax-modal-x { background:none; border:none; cursor:pointer; color:var(--ax-faint); padding:4px; border-radius:8px; display:inline-flex; }
        .adminx .ax-modal-x:hover { background:#f1f5f9; color:#475569; }
        .adminx .ax-modal-body { padding:22px; display:grid; gap:16px; }
        .adminx .ax-modal-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
        .adminx .ax-field label { display:block; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--ax-faint); margin-bottom:6px; }
        .adminx .ax-field input, .adminx .ax-field select { width:100%; border:1px solid #e2e8f0; border-radius:10px; padding:10px 12px; font-size:14px; font-family:inherit; color:var(--ax-ink); outline:none; background:#fff; transition:border .15s, box-shadow .15s; }
        .adminx .ax-field input:focus, .adminx .ax-field select:focus { border-color:var(--brand); box-shadow:0 0 0 3px var(--brand-ring); }
        .adminx .ax-modal-foot { padding:16px 22px; border-top:1px solid #f1f5f9; display:flex; justify-content:flex-end; gap:10px; }

        @media (max-width:720px) {
          .adminx .ax-modal-row { grid-template-columns:1fr; }
          .adminx .ax-table thead { display:none; }
        }
      `}</style>

        {/* No container of its own — AdminLayout supplies it standalone, and
            AdminPartnersHub supplies it when this renders as a tab. */}
        <div>
          {/* Page header */}
          {/* Embedded as a tab it has no page header of its own, so the save/add
              controls ride above the stats. */}
          {embedded && (
            <div className="ax-ph-actions" style={{ justifyContent: 'flex-end', marginBottom: 18 }}>
              {savedAt && <span className="ax-saved"><CheckCircle2 /> Saved {savedAt}</span>}
              <button className="btn" onClick={handleSave} disabled={saving}><Save /> {saving ? 'Saving…' : 'Save changes'}</button>
              <button className="btn btn-pri" onClick={openAdd}><Plus /> Add partner</button>
            </div>
          )}

          {/* Stat cards */}
          <div className="stats">
            {kpis.map(k => (
              <div key={k.label} className="stat">
                <div className="stat-ic">{k.icon}</div>
                <div className="stat-val">{k.value}</div>
                <div className="stat-lbl">{k.label}</div>
              </div>
            ))}
          </div>

          {/* Directory card */}
          <div className="ax-card">
            <div className="ax-toolbar">
              <div className="ax-search">
                <Search />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search partners…" />
              </div>
              <div className="chips">
                {CHIPS.map(c => (
                  <button key={c.label} className={`chip ${chip === c.label ? 'active' : ''}`} onClick={() => setChip(c.label)}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="ax-spin"><div className="ax-spinner" /></div>
            ) : partners.length === 0 ? (
              <div className="ax-empty">
                <Building2 />
                <p>No partners yet</p>
                <span>Add your first partner to start building your network.</span>
                <div style={{ marginTop: 16 }}>
                  <button className="btn btn-pri" onClick={openAdd}><Plus /> Add your first partner</button>
                </div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="ax-empty">
                <Search />
                <p>No partners match</p>
                <span>Try a different search or filter.</span>
              </div>
            ) : (
              <table className="ax-table">
                <thead>
                  <tr>
                    <th>Partner</th>
                    <th>Type</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => {
                    const pill = statusPill(p.status);
                    return (
                      <tr key={p.id}>
                        <td><span className="ax-pname">{p.name || 'Untitled partner'}</span></td>
                        <td><span className="ax-type">{p.type || '-'}</span></td>
                        <td>
                          {p.contactName || p.email ? (
                            <span className="ax-contact">
                              {p.contactName || '-'}
                              {p.email && <small>{p.email}</small>}
                            </span>
                          ) : <span className="ax-muted">-</span>}
                        </td>
                        <td><span className={pill.cls}>{pill.label}</span></td>
                        <td>
                          <div className="ax-actions">
                            <button className="ax-icon-btn" onClick={() => openEdit(p)}><Pencil /> Edit</button>
                            <button className="ax-icon-btn danger" onClick={() => removePartner(p.id, p.name)}><Trash2 /> Remove</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Add / Edit modal */}
        {modalOpen && (
          <div className="ax-overlay" onClick={() => setModalOpen(false)}>
            <div className="ax-modal" onClick={e => e.stopPropagation()}>
              <div className="ax-modal-head">
                <h3>{editingId ? 'Edit partner' : 'Add partner'}</h3>
                <button className="ax-modal-x" onClick={() => setModalOpen(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="ax-modal-body">
                <div className="ax-field">
                  <label>Partner name</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Acme Free Zone" />
                </div>
                <div className="ax-modal-row">
                  <div className="ax-field">
                    <label>Type</label>
                    <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                      {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="ax-field">
                    <label>Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as Draft['status'] }))}>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="paused">Paused</option>
                    </select>
                  </div>
                </div>
                <div className="ax-field">
                  <label>Contact name</label>
                  <input value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))} placeholder="Jane Doe" />
                </div>
                <div className="ax-field">
                  <label>Contact email</label>
                  <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="contact@partner.com" />
                </div>
              </div>
              <div className="ax-modal-foot">
                <button className="btn" onClick={() => setModalOpen(false)}>Cancel</button>
                <button className="btn btn-pri" onClick={submitModal}>
                  {editingId ? 'Save partner' : 'Add partner'}
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  );

  if (embedded) return <div className="adminx">{content}</div>;

  return (
    <AdminLayout
      section="Overview"
      title="Partners"
      icon={Handshake}
      subtitle="Referral partners, free zones, law firms, banks and others you work with."
      actions={<>
        {savedAt && <span className="ax-saved"><CheckCircle2 /> Saved {savedAt}</span>}
        <button className="btn" onClick={handleSave} disabled={saving}><Save /> {saving ? 'Saving…' : 'Save changes'}</button>
        <button className="btn btn-pri" onClick={openAdd}><Plus /> Add partner</button>
      </>}
    >
      {content}
    </AdminLayout>
  );
}
